import { useState } from 'react';
import { FileText, Receipt, X, AlertCircle, Loader2, Ban, Download } from 'lucide-react';
import toast from 'react-hot-toast';
import Button from '@/components/ui/Button';
import {
  usePointsOfSale,
  useCreateInvoiceFromSale,
  useCreateCreditNote,
  useInvoicesForSale,
} from '@/hooks/useBilling';
import { billingService, invoiceLetter, INVOICE_TYPE_LABELS } from '@/services/billing';
import type { DocumentType, ReceiverIvaCondition, Invoice } from '@/services/billing';
import { generarTicketPdf, generarFacturaPdf, condicionLabel } from '@/services/invoicePdf';
import type { ComprobanteItem } from '@/services/invoicePdf';
import type { Sale, Tenant } from '@/types';
import { useAuthStore } from '@/context/authStore';

/**
 * Que comprobante entregarle al cliente despues de cobrar.
 *
 * Son dos cosas distintas y el modal las mantiene separadas a proposito:
 *  - Comprobante comun: PDF interno, sale al instante, no pasa por ARCA.
 *  - Factura fiscal: pide el CAE a ARCA. Si ARCA la aprueba ya no se borra,
 *    se anula con nota de credito. Por eso pide confirmar antes.
 */

interface Props {
  sale: Sale;
  tenant: Tenant;
  onClose: () => void;
}

const PAYMENT_LABELS: Record<string, string> = {
  cash: 'Efectivo',
  card: 'Tarjeta',
  transfer: 'Transferencia',
  mixed: 'Mixto',
};

const money = (n: number): string =>
  '$' + Number(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 });

export default function ComprobanteModal({ sale, tenant, onClose }: Props) {
  const arcaEnabled = !!tenant.has_billing_arca;
  // Configurar la facturacion es del admin; emitir, de quien vende.
  const puedeConfigurar = useAuthStore((s) => s.user?.effective_permissions?.includes('users') ?? false);

  const { data: pointsOfSale = [] } = usePointsOfSale();
  const { data: invoices = [] } = useInvoicesForSale(arcaEnabled ? sale.id : undefined);
  const createInvoice = useCreateInvoiceFromSale();
  const createCreditNote = useCreateCreditNote();

  const [mode, setMode] = useState<'choose' | 'factura'>('choose');
  const [posId, setPosId] = useState<number | ''>('');
  const [docType, setDocType] = useState<DocumentType>('sin_documento');
  const [docNumber, setDocNumber] = useState('');
  const [receiverName, setReceiverName] = useState(sale.owner_name ?? '');
  const [ivaCondition, setIvaCondition] = useState<ReceiverIvaCondition>('consumidor_final');
  const [busy, setBusy] = useState(false);

  const toComprobanteItem = (item: Sale['items'][number]): ComprobanteItem => ({
    descripcion: item.product_name ?? `Producto #${item.product_id}`,
    cantidad: Number(item.quantity),
    precioUnitario: Number(item.unit_price),
    subtotal: Number(item.subtotal),
  });

  const items: ComprobanteItem[] = sale.items.map(toComprobanteItem);

  // Cuando la veterinaria separa servicios de productos en distintos puntos de venta,
  // cada comprobante tiene que mostrar solo las lineas de su rubro.
  const itemsPara = (posId: number): ComprobanteItem[] => {
    const pos = pointsOfSale.find((p) => p.id === posId);
    if (!pos || pos.applies_to === 'all') return items;
    const soloServicios = pos.applies_to === 'services';
    const filtrados = sale.items.filter((i) => !!i.is_service === soloServicios);
    return filtrados.length > 0 ? filtrados.map(toComprobanteItem) : items;
  };

  const activePos = pointsOfSale.filter((p) => p.is_active);

  // Si hay un punto de venta para servicios y otro para productos, el backend manda cada
  // rubro al suyo. Elegir uno a mano no cambia nada, ni siquiera cuando la venta es de un
  // solo rubro: igual se rutea sola. Por eso el desplegable no se muestra en ese caso.
  const posServicios = activePos.find((p) => p.applies_to === 'services');
  const posProductos = activePos.find((p) => p.applies_to === 'products');
  const separaPorRubro = !!posServicios && !!posProductos;
  const facturaPartida =
    separaPorRubro &&
    sale.items.some((i) => i.is_service) &&
    sale.items.some((i) => !i.is_service);

  const effectivePosId =
    posId !== ''
      ? posId
      : separaPorRubro
        ? (posServicios ?? posProductos!).id
        : activePos.length === 1
          ? activePos[0].id
          : '';

  // Facturas ya autorizadas de esta venta (las notas de credito se listan aparte).
  const facturas = invoices.filter(
    (inv) => inv.status === 'approved' && inv.invoice_type.startsWith('factura_'),
  );
  const notasCredito = invoices.filter(
    (inv) => inv.status === 'approved' && inv.invoice_type.startsWith('nota_credito_'),
  );
  // La nota de credito guarda a que factura anula, asi que se ata por id. Antes se
  // adivinaba por punto de venta e importe, que con dos facturas por venta se confunde.
  const yaAnulada = (inv: Invoice) =>
    notasCredito.some((nc) => nc.credited_invoice_id === inv.id);

  const descargarFactura = async (invoiceId: number) => {
    setBusy(true);
    try {
      const detail = await billingService.getInvoice(invoiceId);
      await generarFacturaPdf(
        {
          invoiceTypeLabel: INVOICE_TYPE_LABELS[detail.invoice_type],
          letra: invoiceLetter(detail.invoice_type),
          posNumber: detail.pos_number,
          invoiceNumber: detail.invoice_number ?? 0,
          fecha: detail.invoice_date,
          cae: detail.cae ?? '',
          caeDueDate: detail.cae_due_date,
          qrUrl: detail.qr_url,
          emisorCuit: detail.emisor_cuit,
          emisorRazonSocial: detail.emisor_razon_social,
          emisorIvaCondition: detail.emisor_iva_condition,
          receptorNombre: detail.receiver_name,
          receptorDoc: detail.receiver_doc_number,
          receptorCondicion: condicionLabel(detail.receiver_iva_condition),
          receptorDomicilio: detail.receiver_address,
          items: itemsPara(detail.point_of_sale_id),
          total: Number(detail.total),
        },
        tenant,
      );
    } catch (e: any) {
      toast.error(e?.response?.data?.detail ?? 'No se pudo generar el PDF');
    } finally {
      setBusy(false);
    }
  };

  const handleTicket = () => {
    generarTicketPdf(
      {
        numero: sale.receipt_number ?? sale.id,
        fecha: sale.created_at,
        clienteNombre: sale.owner_name,
        items,
        total: Number(sale.total),
        medioPago: PAYMENT_LABELS[sale.payment_method] ?? sale.payment_method,
      },
      tenant,
    );
    onClose();
  };

  const handleFactura = async () => {
    if (!effectivePosId) {
      toast.error('Elegi el punto de venta');
      return;
    }
    if (docType !== 'sin_documento' && !docNumber.trim()) {
      toast.error('Ingresa el numero de documento');
      return;
    }

    setBusy(true);
    try {
      const emitidas = await createInvoice.mutateAsync({
        saleId: sale.id,
        payload: {
          point_of_sale_id: Number(effectivePosId),
          receiver_name: receiverName.trim() || null,
          receiver_doc_type: docType,
          receiver_doc_number: docType === 'sin_documento' ? null : docNumber.trim(),
          receiver_iva_condition: ivaCondition,
        },
      });

      // Puede volver mas de una si la venta se partio por rubro (servicios / productos).
      const aprobadas = emitidas.filter((inv) => inv.status === 'approved' && inv.cae);
      const rechazadas = emitidas.filter((inv) => inv.status !== 'approved' || !inv.cae);

      // ARCA puede rechazar: el comprobante se guarda igual, pero sin CAE.
      rechazadas.forEach((inv) => toast.error(inv.error_message || 'ARCA rechazo el comprobante'));

      if (aprobadas.length === 0) return;

      if (aprobadas.length === 1) {
        toast.success(
          `Factura ${invoiceLetter(aprobadas[0].invoice_type)} autorizada. CAE ${aprobadas[0].cae}`,
        );
      } else {
        toast.success(`${aprobadas.length} comprobantes autorizados por ARCA`);
      }

      for (const inv of aprobadas) {
        await descargarFactura(inv.id);
      }
      if (rechazadas.length === 0) onClose();
    } catch (e: any) {
      const msg = e?.response?.data?.detail ?? 'No se pudo emitir la factura';
      toast.error(typeof msg === 'string' ? msg : 'Error al emitir la factura');
    } finally {
      setBusy(false);
    }
  };

  const handleNotaCredito = async (invoice: Invoice) => {
    const numero = `${invoice.pos_number.toString().padStart(4, '0')}-${(invoice.invoice_number ?? 0)
      .toString()
      .padStart(8, '0')}`;
    const motivo = window.prompt(
      `Nota de credito por el total de la factura ${numero} (${money(Number(invoice.total))}).\n\n` +
        'Motivo (opcional):',
      '',
    );
    // prompt devuelve null si cancelaron; '' es un motivo vacio valido.
    if (motivo === null) return;

    setBusy(true);
    try {
      const nota = await createCreditNote.mutateAsync({
        invoiceId: invoice.id,
        motivo: motivo.trim() || undefined,
      });
      if (nota.status !== 'approved' || !nota.cae) {
        toast.error(nota.error_message || 'ARCA rechazo la nota de credito');
        return;
      }
      toast.success(`Nota de credito autorizada. CAE ${nota.cae}`);
      await descargarFactura(nota.id);
    } catch (e: any) {
      const msg = e?.response?.data?.detail ?? 'No se pudo emitir la nota de credito';
      toast.error(typeof msg === 'string' ? msg : 'Error al emitir la nota de credito');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-cream-200">
          <h2 className="font-semibold text-taupe-800">
            {mode === 'choose' ? 'Comprobante' : 'Factura fiscal'}
          </h2>
          <button onClick={onClose} className="text-taupe-400 hover:text-taupe-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="text-sm text-taupe-600">
            Venta #{sale.receipt_number ?? sale.id} &mdash; total{' '}
            <span className="font-semibold text-taupe-800">{money(Number(sale.total))}</span>
          </div>

          {mode === 'choose' ? (
            <div className="space-y-3">
              {/* Comprobantes fiscales ya emitidos para esta venta */}
              {facturas.length > 0 ? (
                <div className="space-y-2">
                  <div className="text-xs font-medium uppercase tracking-wide text-taupe-500">
                    Comprobantes fiscales emitidos
                  </div>
                  {facturas.map((inv) => (
                    <div
                      key={inv.id}
                      className="rounded-lg border border-green-200 bg-green-50 p-3 space-y-2"
                    >
                      <div className="text-sm text-green-900">
                        <span className="font-medium">
                          {INVOICE_TYPE_LABELS[inv.invoice_type]}{' '}
                          {inv.pos_number.toString().padStart(4, '0')}-
                          {(inv.invoice_number ?? 0).toString().padStart(8, '0')}
                        </span>
                        <div className="text-xs text-green-700">CAE {inv.cae}</div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="secondary"
                          onClick={() => descargarFactura(inv.id)}
                          disabled={busy}
                        >
                          <Download className="h-4 w-4 mr-1" />
                          Descargar PDF
                        </Button>
                        {!yaAnulada(inv) ? (
                          <Button
                            variant="secondary"
                            onClick={() => handleNotaCredito(inv)}
                            disabled={busy}
                          >
                            <Ban className="h-4 w-4 mr-1" />
                            Nota de credito
                          </Button>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}

              {notasCredito.length > 0 ? (
                <div className="space-y-2">
                  {notasCredito.map((nc) => (
                    <div
                      key={nc.id}
                      className="rounded-lg border border-cream-300 bg-cream-50 p-3 flex items-center justify-between gap-2"
                    >
                      <div className="text-sm text-taupe-700">
                        <span className="font-medium">
                          {INVOICE_TYPE_LABELS[nc.invoice_type]}{' '}
                          {nc.pos_number.toString().padStart(4, '0')}-
                          {(nc.invoice_number ?? 0).toString().padStart(8, '0')}
                        </span>
                        <div className="text-xs text-taupe-500">CAE {nc.cae}</div>
                      </div>
                      <Button
                        variant="secondary"
                        onClick={() => descargarFactura(nc.id)}
                        disabled={busy}
                      >
                        <Download className="h-4 w-4 mr-1" />
                        PDF
                      </Button>
                    </div>
                  ))}
                </div>
              ) : null}

              <button
                onClick={handleTicket}
                className="w-full flex items-start gap-3 p-4 rounded-lg border border-cream-300 hover:border-taupe-400 hover:bg-cream-50 text-left transition"
              >
                <Receipt className="h-5 w-5 text-taupe-600 mt-0.5 shrink-0" />
                <div>
                  <div className="font-medium text-taupe-800">Comprobante comun</div>
                  <div className="text-sm text-taupe-500">
                    Ticket interno de la venta. Sale al instante y no pasa por ARCA.
                  </div>
                </div>
              </button>

              {arcaEnabled && facturas.every(yaAnulada) ? (
                <button
                  onClick={() => setMode('factura')}
                  className="w-full flex items-start gap-3 p-4 rounded-lg border border-cream-300 hover:border-taupe-400 hover:bg-cream-50 text-left transition"
                >
                  <FileText className="h-5 w-5 text-taupe-600 mt-0.5 shrink-0" />
                  <div>
                    <div className="font-medium text-taupe-800">Factura fiscal</div>
                    <div className="text-sm text-taupe-500">
                      Se pide el CAE a ARCA. Es un comprobante fiscal real.
                    </div>
                  </div>
                </button>
              ) : null}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-start gap-2 text-sm bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-3">
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>
                  Una vez que ARCA la autoriza, la factura no se borra: se anula con una nota
                  de credito. Revisa los datos antes de emitir.
                </span>
              </div>

              {separaPorRubro ? (
                <div className="text-sm bg-cream-50 border border-cream-300 text-taupe-700 rounded-lg p-3">
                  {facturaPartida ? (
                    <>
                      Esta venta se va a facturar en <strong>dos comprobantes</strong>: los
                      servicios por su punto de venta y los productos por el suyo.
                    </>
                  ) : (
                    <>
                      El comprobante sale por el punto de venta que corresponde al rubro de
                      la venta.
                    </>
                  )}
                </div>
              ) : null}

              {activePos.length !== 1 && !separaPorRubro ? (
                <div>
                  <label className="block text-sm font-medium text-taupe-700 mb-1">
                    Punto de venta
                  </label>
                  <select
                    value={posId}
                    onChange={(e) => setPosId(e.target.value ? Number(e.target.value) : '')}
                    className="w-full px-3 py-2 border border-cream-300 rounded-lg outline-none focus:ring-2 focus:ring-taupe-400"
                  >
                    <option value="">Seleccionar&hellip;</option>
                    {activePos.map((pos) => (
                      <option key={pos.id} value={pos.id}>
                        {pos.number.toString().padStart(4, '0')} &mdash; {pos.description}
                      </option>
                    ))}
                  </select>
                  {activePos.length === 0 ? (
                    <p className="text-sm text-red-600 mt-1">
                      {/* El empleado no entra a Facturacion, asi que mandarlo ahi no le
                          sirve: se le dice a quien pedirselo. */}
                      No hay puntos de venta cargados.{' '}
                      {puedeConfigurar
                        ? 'Cargalos en Facturación.'
                        : 'Pedile al administrador que los cargue en Facturación.'}
                    </p>
                  ) : null}
                </div>
              ) : null}

              <div>
                <label className="block text-sm font-medium text-taupe-700 mb-1">
                  Nombre del cliente
                </label>
                <input
                  type="text"
                  value={receiverName}
                  onChange={(e) => setReceiverName(e.target.value)}
                  placeholder="Consumidor Final"
                  className="w-full px-3 py-2 border border-cream-300 rounded-lg outline-none focus:ring-2 focus:ring-taupe-400"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-taupe-700 mb-1">Documento</label>
                  <select
                    value={docType}
                    onChange={(e) => setDocType(e.target.value as DocumentType)}
                    className="w-full px-3 py-2 border border-cream-300 rounded-lg outline-none focus:ring-2 focus:ring-taupe-400"
                  >
                    <option value="sin_documento">Sin identificar</option>
                    <option value="dni">DNI</option>
                    <option value="cuit">CUIT</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-taupe-700 mb-1">Numero</label>
                  <input
                    type="text"
                    value={docNumber}
                    onChange={(e) => setDocNumber(e.target.value.replace(/\D/g, ''))}
                    disabled={docType === 'sin_documento'}
                    placeholder={docType === 'cuit' ? '20123456780' : '30123456'}
                    className="w-full px-3 py-2 border border-cream-300 rounded-lg outline-none focus:ring-2 focus:ring-taupe-400 disabled:bg-cream-50 disabled:text-taupe-400"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-taupe-700 mb-1">
                  Condicion frente al IVA
                </label>
                <select
                  value={ivaCondition}
                  onChange={(e) => setIvaCondition(e.target.value as ReceiverIvaCondition)}
                  className="w-full px-3 py-2 border border-cream-300 rounded-lg outline-none focus:ring-2 focus:ring-taupe-400"
                >
                  <option value="consumidor_final">Consumidor Final</option>
                  <option value="monotributo">Monotributo</option>
                  <option value="responsable_inscripto">Responsable Inscripto</option>
                  <option value="exento">Exento</option>
                </select>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-between gap-3 px-5 py-4 border-t border-cream-200">
          <Button variant="secondary" onClick={mode === 'choose' ? onClose : () => setMode('choose')}>
            {mode === 'choose' ? 'Cerrar' : 'Volver'}
          </Button>
          {mode === 'factura' ? (
            <Button onClick={handleFactura} disabled={busy}>
              {busy ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <FileText className="h-4 w-4 mr-2" />
              )}
              {busy ? 'Emitiendo…' : 'Emitir factura'}
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
